/**
 * The links service's per-day visitor id (repo `mmthstudio/links`,
 * `VisitorIDs`), proxied by nginx on the site's own origin.
 */
export const VISITOR_ID_PATH = '/api/links/v1/visitor-id'

/**
 * Prefix of the Amplitude device id built from it — what tells a cookieless
 * web user apart in Amplitude (`device_id` has prefix `visitor-`).
 */
export const VISITOR_DEVICE_ID_PREFIX = 'visitor-'

const VISITOR_ID_PATTERN = /^[0-9a-f]{32}$/

/**
 * The Amplitude device id for a visitor who has not consented to analytics
 * storage: an HMAC of site, network and user agent the server keys with a
 * secret replaced at UTC midnight — the same all day across page loads,
 * stored nowhere on the device. `undefined` when the service is down, slow
 * (`timeoutMs`) or has no address to work with; never throws.
 */
export async function fetchVisitorDeviceId(timeoutMs = 3000): Promise<string | undefined> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeoutMs)
  try {
    const response = await fetch(VISITOR_ID_PATH, {
      cache: 'no-store',
      credentials: 'omit',
      signal: controller.signal,
    })
    if (!response.ok) return undefined
    const body = (await response.json()) as { visitor_id?: unknown }
    const id = body.visitor_id
    return typeof id === 'string' && VISITOR_ID_PATTERN.test(id)
      ? VISITOR_DEVICE_ID_PREFIX + id
      : undefined
  } catch {
    return undefined
  } finally {
    clearTimeout(timer)
  }
}
